import { useStoreWebsiteConversionReport, type SupabaseClient } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useWebsiteT } from '../hooks/use-website-t';

export interface ConversionReportDialogProps {
  readonly client: SupabaseClient;
  readonly storeId: string;
  readonly onClose: () => void;
}

/** What the conversion of the old design kept, and what it locked. Counts only, never content. */
export function ConversionReportDialog({
  client,
  storeId,
  onClose,
}: ConversionReportDialogProps): React.JSX.Element {
  const { t } = useWebsiteT();
  const report = useStoreWebsiteConversionReport(client, storeId);
  const data = report.data ?? null;

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent data-testid="conversion-report-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.websiteTemplates.report.title')}</DialogTitle>
          <DialogDescription>{t('admin.websiteTemplates.editor.legacyNotice')}</DialogDescription>
        </DialogHeader>
        {report.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : report.isError ? (
          <p role="alert" className="text-destructive text-sm">
            {t('admin.websiteTemplates.errors.generic')}
          </p>
        ) : data ? (
          <ul className="space-y-1 text-sm">
            <li>{t('admin.websiteTemplates.report.mapped', { count: data.mappedSlots })}</li>
            <li>{t('admin.websiteTemplates.report.frozen', { count: data.frozenItems })}</li>
            <li>
              {t('admin.websiteTemplates.report.sourceLanguage', {
                language: t(`admin.websiteTemplates.locale.${data.sourceLocale}`),
              })}
            </li>
          </ul>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('admin.websiteTemplates.common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
